import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, ElementRef,
  Inject, AfterViewInit, OnInit, OnDestroy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { zip } from 'rxjs';
import { map, take, switchMap, tap } from 'rxjs/operators';

import { isNullOrUndefined } from '@app/util/util';
import { BusItem, BusResultsMobile, MobileSector, MobileSectorVm } from '@app/entities/bus-results';
import { AppFacade } from '@app/app.facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { ANIMATIONS, TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { TripTypes } from '@app/entities/trip-types';
import { BusResultsFacade } from '@buses/bus-results/bus-results.facade';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { bookingRootRoute } from '@booking/booking-routing.module';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { ProductProperties } from '@avo/interface/trigger-props';
import { busesCabinsMapper } from '@flight-results/mappers/buses.mapper';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { SOURCE_PARAM } from '@app/modules/core/constants/query-params';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AVO_EVENTS } from '@avo/events/avo.events';

@Component({
  selector: 'app-bus-results-mobile-itinerary-route-detail',
  templateUrl: './bus-results-mobile-itinerary-route-detail.component.html',
  styleUrls: ['./bus-results-mobile-itinerary-route-detail.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BusResultsMobileItineraryRouteDetailComponent implements AfterViewInit, OnInit, OnDestroy {

  @ViewChild('container') public container: ElementRef;
  @ViewChild('bodycontainer') public bodyContainer: ElementRef;

  public routes: MobileSectorVm[];
  public busClass: FlightClass;
  public provider: string;
  public animation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public price: string;
  public routeSectorNumberSelected: number;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  private buses: BusItem[] = [];

  constructor(
    private appFacade: AppFacade,
    private facade: BusResultsFacade,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private domManipulateService: DomManipulateService,
    private amplitude: PropertiesAmplitudeService,
    private triggerAmplitudeService: TriggerAmplitudeService,
    private readonly avoService: AvoService,
    @Inject(IS_BROWSER) public isBrowser: boolean) {
    if (this.router.getCurrentNavigation()?.extras?.state && this.router.getCurrentNavigation()?.extras?.state?.routes) {
      this.routes = this.router.getCurrentNavigation().extras.state.routes;
    }
    if (this.router.getCurrentNavigation()?.extras?.state && this.router.getCurrentNavigation()?.extras?.state?.options) {
      this.routes = this.router.getCurrentNavigation().extras?.state?.options;
    }
    this.busClass = this.router.getCurrentNavigation()?.extras?.state?.flightClass;
    this.provider = this.router.getCurrentNavigation()?.extras?.state?.provider;
    this.price = this.router.getCurrentNavigation()?.extras?.state?.price;
    this.buses = this.routes && this.routes.map(route => route.FlightItem);
    this.routeSectorNumberSelected = this.router.getCurrentNavigation()?.extras?.state?.routeSectorNumberSelected;
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      const positionRouteSelected = this.routes?.findIndex(route => route.SectorNumber === this.routeSectorNumberSelected);
      if (positionRouteSelected > -1) {
        this.scrollToPosition(this.bodyContainer.nativeElement.children[positionRouteSelected].offsetTop);
      }
      zip(
        this.activatedRoute.queryParamMap.pipe(take(1)),
        this.amplitude.searchProperties$(SearchBy.BUSES)
      ).pipe(
        switchMap(([queries, searchProps]) => this.avoService.getEventTriggerProps$(
          AVO_EVENTS.VIEW_TRAVEL_ITEM_DETAIL, { SOURCE: queries.get(SOURCE_PARAM) },
          searchProps, this.getProductProperties()
        )),
        tap(trigger => {
          this.AvoDispatch.viewTravelItemDetail({
            ...this.avoService.avoBaseProperties(trigger),
            ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
            ...this.avoService.avoProductProps(trigger?.PRODUCT_PROPS),
            source: this.avoService.getAvoSourceValueType(trigger?.EVENT_PROPS?.SOURCE),
            index: trigger?.EVENT_PROPS?.INDEX as number
          });
        })
      ).subscribe().unsubscribe();
    }
  }

  ngOnInit(): void {
    this.domManipulateService.setBodyScroll(false);
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
  }

  private getProductProperties(): ProductProperties {
    const routes = this.routes;
    if (isNullOrUndefined(routes) || routes.length === 0) return { TRAVEL_PRODUCT_PROVIDER: this.provider };
    const flights = routes[0].Flights;
    if (isNullOrUndefined(flights) || flights.length === 0) return { TRAVEL_PRODUCT_PROVIDER: this.provider };
    const item = flights[0];
    return {
      TRAVEL_PRODUCT_CODE: item.OpAirlineCode,
      TRAVEL_PRODUCT_CATEGORY: busesCabinsMapper(routes).join(','),
      TRAVEL_PRODUCT_PROVIDER: this.provider,
    };
  }

  public goBack(): void {
    this.animation = ANIMATIONS.SLIDE_OUT_RIGHT;
    this.domManipulateService.setBodyScroll(true);
    setTimeout(() => this.appFacade.navigateToLastRoute(), TIME_TO_SLIDE);
  }

  public tripTypeHeader(route: MobileSectorVm): string {
    return route.SectorNumber === TripTypes.DEPARTURE ?
      'BUS_RESULTS_MODULE.ITINERARY_LIST_SECTION.DEPARTURE_TRIP' :
      'BUS_RESULTS_MODULE.ITINERARY_LIST_SECTION.RETURN_TRIP';
  }

  public isReturnHeader(route: MobileSectorVm): boolean {
    return route.SectorNumber !== TripTypes.DEPARTURE;
  }

  public selectBus(): void {
    zip(
      this.facade.searchPayload$.pipe(take(1)),
      this.amplitude.searchProperties$(SearchBy.BUSES),
      this.facade.busesResultsMobile$.pipe(map(results => this.formatBusResultsMobile(results, this.buses)))
    ).pipe(
      switchMap(([payload, searchProps, { index }]) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
        AMPLITUDE_EVENTS.ADD_TO_CART, { TRAVEL_PRODUCT_TYPE: SearchBy.BUSES, INDEX: index }, searchProps
      ).pipe(
        tap(trigger => {
          triggerAmplitudeEventApp(trigger);
          this.router.navigate([bookingRootRoute, payload.SessionToken,
            payload.SearchId, this.buses[0].OptionId, 'null', TravelBookingType.BUSES]);
        })
      ))
    ).subscribe().unsubscribe();
  }

  public scrollToPosition(top: number): void {
    this.bodyContainer.nativeElement.scrollTo({
      left: 0, top: top
    });
  }

  private formatBusResultsMobile(results: BusResultsMobile[],
    selectedFlights: BusItem[]): { options: MobileSector[], provider: string, index: number } {
    const index = results.findIndex(f => f.OptionId === selectedFlights[0].OptionId) + 1;
    const option = results[index];
    return { options: option?.Sectors, provider: option?.Provider, index };
  }
}
